import { DownOutlined } from "@payping/icons";
import { ValidEmail, ValidMobileNumber } from "@payping/utils/validation";
import { DashboardRoutes } from "@/router/constant";
import { Button, Drawer, Form, Space } from "@payping/web-components";
import { UploadFile } from "@payping/web-components/components/Upload/types";
import classNames from "classnames";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAsyncFn } from "react-use";

import styles from "@/assets/scss/dashCustomer/form.module.scss";
import { useAppTranslation } from "@/utils/useAppTranslation";
import HandlingCrispInMobile from "@/components/mobile/HandlingCrispInMobile";
import DrawerCloseButton from "@/components/Buttons/DrawerCloseButton";
import { CustomerFormProps, CustomerFormTypes } from "../types";
import BaseForm from "./Base";
import ExpandForm from "./Expand";

const CustomerForm: React.FC<CustomerFormProps> = (props: CustomerFormProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const { details, onOk, loading: loadingDetails, locationSearch, title } = props;
  const [phoneHelp, setPhoneHelp] = useState<string>();
  const [emailHelp, setEmailHelp] = useState<string>();
  const [expanded, setExpanded] = useState<boolean>(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const navigate = useNavigate();
  const { t } = useAppTranslation();
  const [form] = Form.useForm<CustomerFormTypes>();
  const ROUTES = DashboardRoutes.myShop.customer;

  function goToList() {
    navigate({
      pathname: ROUTES.list.url,
      search: locationSearch,
    });
  }

  useEffect(() => {
    if (details) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { userPhotoFileId, userPhotoFileAddress, ...data } = details;
      form.setFieldsValue({
        ...data,
        avatar: userPhotoFileAddress,
        tags: data.tags?.map((tag) => tag.id),
      });
      if (userPhotoFileAddress) {
        setFileList([
          {
            name: "image",
            size: 0,
            type: "image/*",
            uid: "0",
            url: userPhotoFileAddress,
          },
        ]);
      }
    }
  }, [details, form]);

  const checkValidation = useCallback(
    (phone: string, email: string) => {
      let isValid = true;
      if (!email && !phone) {
        setPhoneHelp(t("error.phoneOrEmailRequired"));
        setEmailHelp(t("error.phoneOrEmailRequired"));
        isValid = false;
      } else if (email) {
        if (!ValidEmail(email)) {
          setEmailHelp(t("error.email"));
          isValid = false;
        } else {
          setEmailHelp("");
          setPhoneHelp("");
        }
      } else if (phone) {
        if (!ValidMobileNumber(phone)) {
          setPhoneHelp(t("error.phone"));
          isValid = false;
        } else {
          setEmailHelp("");
          setPhoneHelp("");
        }
      }
      return isValid;
    },
    [t],
  );

  const [{ loading: loadingSubmit }, handleSubmit] = useAsyncFn(
    async (values: CustomerFormTypes): Promise<void> => {
      const { avatar, tags: tagIds, ...data } = values;
      if (checkValidation(data.phone, data.email)) {
        await onOk({
          code: details?.code,
          ...data,
          tagIds,
          userPhotoFileId:
            typeof avatar === "string"
              ? avatar.split("/")[avatar.split("/").length - 1]
              : avatar?.fileList[0]?.response,
        });
        goToList();
      }
    },
    [details],
  );

  const loading = useMemo(() => loadingDetails || loadingSubmit, [loadingDetails, loadingSubmit]);

  return (
    <Drawer
      open={isDrawerOpen}
      title={title}
      height={"100%"}
      closeIcon={false}
      placement={"bottom"}
      extra={<DrawerCloseButton setIsDrawerOpen={setIsDrawerOpen} />}
    >
      <HandlingCrispInMobile>
        <Form<CustomerFormTypes>
          canDeactivate={true}
          className={classNames(styles.form, "tw-w-full")}
          onValuesChange={(changedValues, values): void => {
            if (Object.keys(changedValues).find((value) => value === "phone" || value === "email")) {
              checkValidation(values.phone, values.email);
            }
          }}
          initialValues={{
            isBusiness: false,
            tags: [],
          }}
          onFinish={handleSubmit}
          form={form}
          layout={"vertical"}
        >
          <BaseForm
            phoneHelp={phoneHelp}
            emailHelp={emailHelp}
            loading={loading}
            fileList={fileList}
            setFileList={setFileList}
          />
          <ExpandForm className={expanded ? "" : "display-none"} loading={loading} form={form} />
          <Form.Item>
            <Button variant={"link"} onClick={(): void => setExpanded((prevState) => !prevState)}>
              <Space>
                {expanded ? t("customer.form.less") : t("customer.form.more")}
                <DownOutlined
                  width={"14px"}
                  style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "0.2s" }}
                />
              </Space>
            </Button>
          </Form.Item>
          <Space>
            <Button type={"primary"} htmlType={"submit"} loading={loading}>
              {t("save")}
            </Button>
            <Link to={{ pathname: ROUTES.list.url, search: locationSearch }}>
              <Button variant={"outlined"} color="primary">
                {t("return")}
              </Button>
            </Link>
          </Space>
        </Form>
      </HandlingCrispInMobile>
    </Drawer>
  );
};

export default CustomerForm;
